import { z } from "zod";

/** How often a visible tab sends a heartbeat. */
export const HEARTBEAT_INTERVAL_SECONDS = 30;

const Pixels = z.int().nonnegative().max(100_000);

export const UserAgentHints = z
  .strictObject({
    brands: z
      .array(z.strictObject({ brand: z.string().max(100), version: z.string().max(50) }))
      .max(10)
      .meta({ description: "`navigator.userAgentData` full version list." }),
    platform: z.string().max(50).meta({ example: "macOS" }),
    platformVersion: z.string().max(50).meta({ example: "15.6.0" }),
    mobile: z.boolean(),
    model: z.string().max(100).meta({ description: "The device model; empty on desktops." }),
  })
  .meta({
    id: "UserAgentHints",
    description:
      "Chromium's client hints, which carry the real browser and OS versions its user agent string freezes.",
  });
export type UserAgentHints = z.infer<typeof UserAgentHints>;

export const RecordHeartbeatBody = z
  .strictObject({
    deviceId: z.uuid().meta({
      description: "The browser's id, kept in localStorage.",
      example: "0b7c5a4e-3f1d-4c55-9a53-2f8f0b0d9a61",
    }),
    tabSessionId: z.uuid().meta({
      description:
        "The tab's `rum.sessionId`, which joins the heartbeat to that tab's traces and recording.",
      example: "c6ab9f98-5d85-4efd-9cee-63189c67ed98",
    }),
    url: z.string().min(1).max(2_000).meta({
      description: "The path and query the tab shows.",
      example: "/play/e77758f8-bb2d-4f86-9880-64f07953d818?panel=chat",
    }),
    screen: z.string().min(1).max(200).meta({
      description: "The route the tab shows, as its template rather than its URL.",
      example: "/play/$gameId",
    }),
    gameId: z.uuid().nullable().meta({
      description: "The game the tab shows, when it shows one.",
      example: "e77758f8-bb2d-4f86-9880-64f07953d818",
    }),
    gameLive: z.boolean().meta({
      description: "Whether the tab holds a live socket to the game, so the player is in it.",
      example: true,
    }),
    focused: z
      .boolean()
      .meta({ description: "Whether the tab's window has focus.", example: true }),
    inputIdleSeconds: z.int().nonnegative().nullable().meta({
      description:
        "Seconds since the last pointer, key, wheel or touch input in the tab, or null before any.",
      example: 12,
    }),
    language: z.string().max(35).meta({ description: "`navigator.language`.", example: "en-US" }),
    timezone: z.string().max(64).meta({
      description: "The browser's IANA time zone.",
      example: "America/Los_Angeles",
    }),
    viewport: z.strictObject({ width: Pixels, height: Pixels }).meta({
      description: "The window's inner size in CSS pixels.",
    }),
    display: z
      .strictObject({ width: Pixels, height: Pixels, pixelRatio: z.number().positive() })
      .meta({
        description: "The screen's size in CSS pixels and its device pixel ratio.",
      }),
    userAgentHints: UserAgentHints.nullable(),
  })
  .meta({
    id: "RecordHeartbeatBody",
    description: `What one visible tab reports every ${HEARTBEAT_INTERVAL_SECONDS} seconds.`,
  });
export type RecordHeartbeatBody = z.infer<typeof RecordHeartbeatBody>;
